// Runtime configuration, resolved once at module load.
//
// window.__ENV__ is written by docker-entrypoint.sh at container start from the
// k8s deployment env, so the same image serves every environment. process.env is
// the Parcel build-time fallback (baked in via the Dockerfile ARG) for local dev
// and any image built before the runtime mechanism existed.

const runtime = (typeof window !== 'undefined' && window.__ENV__) || {};

// Full GraphQL endpoint including /graphql: callers POST to it directly, and the
// WebSocket base is derived by stripping that suffix.
export const BFF_URL =
  runtime.BFF_URL || process.env.BFF_URL || 'https://capi.parsinabot.ir/graphql';

export const BFF_WS_BASE =
  process.env.BFF_WS_URL ||
  BFF_URL.replace(/^https/, 'wss').replace(/^http/, 'ws').replace('/graphql', '');

// Origin the join QR code points at (`${PATIENT_ORIGIN}/join/${invitationCode}`).
// Per-environment, so a scanned QR opens the right patient app.
export const PATIENT_ORIGIN =
  runtime.PATIENT_ORIGIN ||
  process.env.PATIENT_ORIGIN ||
  process.env.PATIENT_APP_URL ||
  'https://patient.parsinabot.ir';

// Keycloak origin, no realm path — auth.js appends /realms/<realm>. This used to
// be a bare constant in auth.js, which meant moving SSO host required editing and
// rebuilding the client; routing it through here lets the deployment decide.
// KEYCLOAK_URL is the brand-neutral name and wins; PARSINA_KC_BASE_URL is the one
// every existing deployment sets, and it keeps working.
export const KC_BASE_URL =
  runtime.KEYCLOAK_URL ||
  runtime.PARSINA_KC_BASE_URL ||
  process.env.PARSINA_KC_BASE_URL ||
  'https://sso.itechtrade.ir';

// ── Keycloak identity: realm, client, roles client ──────────────────────────
// Which realm this panel signs in to, which client it signs in as, and which
// client's roles the BFFs read off the token. Parsina's own values are the
// fallbacks, so an image run with none of these set signs in exactly as before;
// a deployment on another realm sets them at container start, same image.
//
// Each lookup takes the first value that is set: the brand-neutral name in
// env.js, the legacy PARSINA_* name in env.js, what the image was built with,
// today's literal. docker-entrypoint.sh writes these keys only when they are
// set, so "nothing set" really does reach the build-time value.
//
// Read on every call, not once at load like the values above: what decides is
// the env.js of the page that is running, and a test can set its own.
const runtimeNow = () => (typeof window !== 'undefined' && window.__ENV__) || {};

/** The realm: the path segment after /realms/. */
export function kcRealm() {
  const env = runtimeNow();
  return env.KEYCLOAK_REALM || env.PARSINA_KC_REALM
    || process.env.KEYCLOAK_REALM || process.env.PARSINA_KC_REALM
    || 'parsina';
}

/**
 * The OIDC client this panel signs in as.
 *
 * The image bakes one in at build time (Dockerfile ARG PARSINA_KC_CLIENT_ID,
 * default `parsina-clinic`), and that baked value is what production signs in
 * with; `parsina-web` is only what a build without it gets (`npm run dev`).
 * There is deliberately no build-time KEYCLOAK_CLIENT_ID: Parcel inlines the
 * whole build environment, and in a BFF's environment that name means the roles
 * client, so a local build from such a shell would sign in as `parsina-bff`.
 */
export function kcClientId() {
  const env = runtimeNow();
  return env.KEYCLOAK_CLIENT_ID || env.PARSINA_KC_CLIENT_ID
    || process.env.PARSINA_KC_CLIENT_ID
    || 'parsina-web';
}

/**
 * The client whose roles the BFFs grant and read (`resource_access[<it>].roles`).
 * It has to name the same client as the BFFs' own setting, or the panel waits
 * for a role that was granted under another name.
 */
export function kcBffClientId() {
  return runtimeNow().KEYCLOAK_BFF_CLIENT_ID || 'parsina-bff';
}

// ── Finding a clinic on the map ──────────────────────────────────────────────
//
// Three values, all per-environment, because every one of them is a host this
// app talks to from inside Iran and any of them may need to become a mirror or
// an Iranian provider without rebuilding the image.
//
// GEOCODER      which adapter in lib/geocode.js answers "what is at this point".
//               A keyed provider (Neshan, map.ir) must be added as a BFF-backed
//               adapter, never as a URL with a key in it — the bundle is public.
// GEOCODER_URL  overrides the default endpoint of the chosen adapter, so an OSM
//               mirror can be pointed at without a code change.
// MAP_TILE_URL  the Leaflet tile template. The map is a convenience: when the
//               tiles or the library cannot be reached the picker says so and
//               «موقعیت فعلی من» keeps working, because that path needs no map.
export const GEOCODER = runtime.GEOCODER || process.env.GEOCODER || 'nominatim';

export const GEOCODER_URL = runtime.GEOCODER_URL || process.env.GEOCODER_URL || '';

// A value without {z}/{x}/{y} is not a tile template, so it falls back to the
// default instead of silently drawing grey squares — images whose entrypoint
// truncated it to "https://{s" are still running.
const isTileTemplate = (v) => typeof v === 'string' && /\{z\}/.test(v) && /\{x\}/.test(v) && /\{y\}/.test(v);

export const MAP_TILE_URL =
  [runtime.MAP_TILE_URL, process.env.MAP_TILE_URL].find(isTileTemplate) ||
  'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

// Leaflet is loaded at runtime rather than bundled: the map is optional, and a
// 150 KB library that most sessions never open should not sit in the entry
// bundle of a reception desk's first paint. Overridable so an environment that
// cannot reach the CDN can serve it from its own origin.
export const MAP_LIB_BASE = (
  runtime.MAP_LIB_BASE || process.env.MAP_LIB_BASE || 'https://unpkg.com/leaflet@1.9.4/dist'
).replace(/\/+$/, '');

// Origin of حساب پارسینا — where a person edits their own identity, password,
// language and two-factor, for every panel at once. Supplied per environment by
// the deployment, exactly like BFF_URL, rather than baked into the image: a
// beta1 panel linking to the staging account app would hand someone a session
// against another environment's data.
//
// Like the clinic switch, this is a plain navigation and not a token hand-off —
// same realm, so the account app runs its own PKCE flow and Keycloak's SSO
// session returns a token without a second sign-in.
//
// No trailing slash — callers append the path.
export const ACCOUNT_APP_URL = (
  runtime.ACCOUNT_APP_URL ||
  process.env.ACCOUNT_APP_URL ||
  'https://account.parsinabot.ir'
).replace(/\/+$/, '');

/**
 * A link into the account app that can find its way back here.
 *
 * `return_url` is checked SERVER-SIDE by the account app against its own
 * allowlist; this origin is already on it for both environments. An unlisted
 * host does not error — the «بازگشت» chip simply does not render — so a new
 * panel hostname needs adding there before its link is useful.
 */
export function accountUrl(section = '/') {
  const back = typeof window !== 'undefined' ? window.location.origin : '';
  const path = section.startsWith('/') ? section : `/${section}`;
  return `${ACCOUNT_APP_URL}${path}${back ? `?return_url=${encodeURIComponent(back)}` : ''}`;
}

// Origin of the DOCTOR panel. The visit room and the patient record are NOT
// reached through it any more — they are the shared clinical module, rendered
// inside this panel (docs/CLINICAL_KIT_DESIGN.md). What is left over there is
// the practitioner's PERSONAL settings: their پارسه wallet and their visit
// layout, which a visit here may still send them to (see lib/doctorPanel.js).
//
// The default is DERIVED from this panel's own hostname, because the two panels
// are deployed as siblings in every environment — clinic.… ↔ doctor.…,
// beta1.clinic.… ↔ beta1.doctor.… — and a derived origin can never point a beta1
// desk at staging's patients. `DOCTOR_APP_URL` overrides it (local dev, a
// differently-named host).
//
// No trailing slash — callers append the path.
export function siblingDoctorOrigin(loc) {
  const host = loc?.hostname || '';
  if (!/(^|\.)clinic\./.test(host)) return '';
  return `${loc.protocol}//${host.replace(/(^|\.)clinic\./, '$1doctor.')}`;
}

export const DOCTOR_APP_URL = (
  runtime.DOCTOR_APP_URL ||
  process.env.DOCTOR_APP_URL ||
  siblingDoctorOrigin(typeof window !== 'undefined' ? window.location : null) ||
  'https://doctor.parsinabot.ir'
).replace(/\/+$/, '');

// The DOCTOR BFF — where the shared clinical screens get their data.
//
// The visit room and the patient record run inside this panel but talk to the
// doctor BFF, never this panel's (lib/clinicalHost.js says why). Derived from
// BFF_URL because the pair is deployed side by side in every environment —
// capi.… ↔ api.…, beta1.capi.… ↔ beta1.api.… — so a derived address can never
// send a beta1 visit to staging. Locally the pair is :4002 ↔ :4000 (CLAUDE.md),
// and that is derived too — a dev box must never fall through to production.
// `DOCTOR_BFF_URL` overrides it.
export function siblingDoctorBff(clinicBffUrl) {
  const url = String(clinicBffUrl || '');
  if (/\/\/(?:[^/]*\.)?capi\./.test(url)) return url.replace(/(\/\/(?:[^/]*\.)?)capi\./, '$1api.');
  if (/\/\/(?:localhost|127\.0\.0\.1):4002(?=\/|$)/.test(url)) return url.replace(':4002', ':4000');
  return '';
}

export const DOCTOR_BFF_URL =
  runtime.DOCTOR_BFF_URL ||
  process.env.DOCTOR_BFF_URL ||
  siblingDoctorBff(BFF_URL) ||
  'https://api.parsinabot.ir/graphql';
